<template>
  <div class="page flex-col">
    <AppHeader />

    <div class="ad-banner">
      <div class="ad-banner-mask">
        <span class="ad-banner-zh">{{ isEnglish ? 'Product Capability Matrix' : '产品能力矩阵' }}</span>
        <span class="ad-banner-en">{{ isEnglish ? 'Counter-UAS' : 'Product Capability Matrix' }}</span>
      </div>
    </div>

    <div class="ad-main">
      <nav class="ad-breadcrumb">
        <router-link :to="homeLink">{{ isEnglish ? 'Home' : '首页' }}</router-link>
        <span class="ad-sep">&gt;</span>
        <span class="ad-crumb-active">
          {{ isEnglish ? 'Product Capability Matrix' : '产品能力矩阵' }}
        </span>
      </nav>

      <div class="ad-title-row">
        <span class="ad-title-zh">{{ isEnglish ? 'Capability Matrix' : '产品能力矩阵' }}</span>
        <span class="ad-title-en">MATRIX</span>
      </div>
      <div class="ad-title-line"></div>

      <div class="ad-body">
        <AntiDroneSidebar active="capability" />

        <div class="ad-content">
          <div class="ad-table-card">
            <table class="capability-table">
              <thead>
                <tr>
                  <th class="th-series">{{ isEnglish ? 'Series' : '产品系列' }}</th>
                  <th class="th-product">{{ isEnglish ? 'Product' : '产品' }}</th>
                  <th class="th-model">{{ isEnglish ? 'Model' : '型号' }}</th>
                  <th
                    v-for="(col, index) in displayColumns"
                    :key="'col_' + index"
                    class="th-cap"
                  >
                    <span v-html="formatColumn(col)"></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in productRows" :key="'prod_' + index">
                  <td
                    v-if="row.showSeries"
                    :rowspan="row.seriesRowspan"
                    class="td-series"
                  >
                    {{ displaySeries(row.item) }}
                  </td>
                  <td class="td-product">
                    {{ isEnglish ? row.item.name : (row.item.nameZh || row.item.name) }}
                  </td>
                  <td class="td-model">{{ row.item.model || '—' }}</td>
                  <td
                    v-for="(cap, cIndex) in row.item.capabilities"
                    :key="'cap_' + index + '_' + cIndex"
                    class="td-cap"
                  >
                    <span class="symbol">{{ cap }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="ad-legend">
            <div v-for="(item, index) in legend" :key="'lg_' + index" class="legend-item">
              <span class="legend-symbol">{{ item.symbol }}</span>
              <span>{{ isEnglish ? item.label : (item.labelZh || item.label) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AntiDroneSidebar from '@/components/AntiDroneSidebar.vue';
import { isEnglishRoute, siteHomeLink } from '@/utils/lang';
import { buildSeriesRowGroups } from '@/utils/matrixRowspan';

export default {
  name: 'ProductCapabilityMatrix',
  components: { AntiDroneSidebar },
  data() {
    return {
      columnsEn: [],
      columnsZh: [],
      products: [],
      legend: []
    };
  },
  computed: {
    isEnglish() {
      return isEnglishRoute(this.$route);
    },
    homeLink() {
      return siteHomeLink(this.$route);
    },
    displayColumns() {
      if (this.isEnglish) return this.columnsEn;
      return this.columnsZh.length ? this.columnsZh : this.columnsEn;
    },
    productRows() {
      return buildSeriesRowGroups(this.products, (item) => this.seriesKey(item));
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const res = await fetch('/data/antidrone.json');
        if (!res.ok) return;
        const data = await res.json();
        const matrix = data.capabilityMatrix || {};
        this.columnsEn = matrix.columns || [];
        this.columnsZh = matrix.columnsZh || [];
        this.products = matrix.products || [];
        this.legend = matrix.legend || [];
      } catch (err) {
        console.warn('Failed to fetch antidrone.json:', err);
      }
    },
    formatColumn(col) {
      return String(col).replace(/\s+/g, '<br />');
    },
    displaySeries(item) {
      if (!item) return '—';
      if (this.isEnglish) return item.seriesEn || item.series || '—';
      return item.series || item.seriesEn || '—';
    },
    seriesKey(item) {
      if (!item) return '';
      if (this.isEnglish) return item.seriesEn || item.series || '';
      return item.series || item.seriesEn || '';
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

.ad-banner {
  width: 100%;
  height: 360px;
  margin-top: 8px;
  background: #1e40af url(./assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.ad-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.ad-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.ad-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.ad-banner-en {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 500;
}

.ad-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 44px 0 72px;
  box-sizing: border-box;
}

.ad-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

.ad-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.ad-breadcrumb a:hover {
  color: #1e40af;
}

.ad-sep {
  color: #9ca3af;
}

.ad-crumb-active {
  color: #1e40af;
}

.ad-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.ad-title-zh {
  font-size: 32px;
  font-weight: 900;
  color: #1e40af;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.ad-title-en {
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 1px;
}

.ad-title-line {
  width: 100%;
  height: 2px;
  background: #93c5fd;
  margin: 16px 0 32px;
}

.ad-body {
  width: 100%;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 56px;
  align-items: start;
}

.ad-content {
  min-width: 0;
}

.ad-table-card {
  width: 100%;
  border: 1px solid #9baada;
  border-radius: 4px;
  overflow-x: auto;
  background: #fff;
}

.capability-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 1180px;
}

.capability-table th,
.capability-table td {
  border: 1px solid #9baada;
  box-sizing: border-box;
}

.capability-table th {
  background: #f5f8ff;
  color: #222;
  font-size: 13px;
  font-weight: 700;
  vertical-align: middle;
  padding: 12px 6px;
  line-height: 1.3;
  text-align: center;
}

.th-series {
  width: 11%;
  text-align: left !important;
  padding-left: 12px !important;
}

.th-product {
  width: 16%;
  text-align: left !important;
  padding-left: 12px !important;
}

.th-model {
  width: 8%;
}

.th-cap {
  width: 5.4%;
}

.capability-table td {
  height: 48px;
  vertical-align: middle;
  padding: 8px;
}

.td-series,
.td-product {
  text-align: left;
  padding-left: 12px !important;
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
}

.td-series {
  vertical-align: middle;
  font-weight: 600;
  color: #1e3a8a;
  background: #f8fafc;
}

.td-model {
  text-align: center;
  color: #1e40af;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.td-cap {
  text-align: center;
}

.td-cap .symbol {
  color: #000;
  font-size: 15px;
}

.capability-table tbody tr:hover {
  background: #f8fafc;
}

.ad-legend {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 20px;
  font-size: 13px;
  color: #4b5563;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-symbol {
  font-weight: 700;
  color: #111827;
}

@media (max-width: 1100px) {
  .ad-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .ad-banner {
    height: 220px;
  }

  .ad-banner-zh {
    font-size: 28px;
  }
}
</style>
